import { useCallback, useEffect, useMemo, useState } from 'react';
import { rosterApi } from '../api/roster.api';
import {
  startOfWeek, startOfMonth, addDays, isoDate, daysInRange, monthGrid,
  formatMonthLabel, periodBounds,
} from '../utils/rosterDates';
import { toast } from 'sonner';

function normalizeStaff(raw) {
  return (Array.isArray(raw) ? raw : []).map((d) => ({
    id: d.id || d._id,
    name: d.name,
    specialization: d.specialization || d.specialisation || d.department || '',
  })).filter((d) => d.id && d.name);
}

/** Roster calendar data for one workforce track. */
export default function useRosterData(track) {
  const trackId = track?.id || 'doctor';
  const [period, setPeriod] = useState('week');
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const [staffId, setStaffId] = useState('');
  const [specialisation, setSpecialisation] = useState('');
  const [entries, setEntries] = useState([]);
  const [staffRaw, setStaffRaw] = useState([]);
  const [loading, setLoading] = useState(false);

  const days = useMemo(() => (
    period === 'month' ? monthGrid(anchor) : daysInRange(startOfWeek(anchor), 7)
  ), [anchor, period]);

  const fromIso = isoDate(days[0]);
  const toIso = isoDate(days[days.length - 1]);
  const { fromIso: publishFromIso, toIso: publishToIso } = useMemo(
    () => periodBounds(period, anchor),
    [period, anchor],
  );
  const rangeLabel = period === 'month'
    ? formatMonthLabel(anchor)
    : `${isoDate(days[0])} → ${isoDate(days[6])}`;

  useEffect(() => {
    setStaffId('');
    setSpecialisation('');
  }, [trackId]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { entries: rows } = await rosterApi.list({
        from_date: fromIso, to_date: toIso, role: trackId,
      });
      setEntries(rows || []);
    } catch (_) {
      toast.error('Could not load roster');
    }
    setLoading(false);
  }, [fromIso, toIso, trackId]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    let cancelled = false;
    const load = track?.clinicRole
      ? rosterApi.staff({ clinic_role: track.clinicRole }).then((d) => d.staff || [])
      : rosterApi.doctors().then((d) => d.doctors || d || []);
    load.then((rows) => { if (!cancelled) setStaffRaw(rows); })
      .catch(() => { if (!cancelled) setStaffRaw([]); });
    return () => { cancelled = true; };
  }, [track?.clinicRole, trackId]);

  const staffList = useMemo(() => normalizeStaff(staffRaw), [staffRaw]);

  const specialisations = useMemo(() => {
    if (!track?.bookingAware) return [];
    return Array.from(new Set(staffList.map((d) => d.specialization).filter(Boolean))).sort();
  }, [staffList, track?.bookingAware]);

  const filteredEntries = useMemo(() => entries.filter((e) => {
    if (staffId && e.staff_id !== staffId) return false;
    if (specialisation && (e.specialisation || '') !== specialisation) return false;
    return true;
  }), [entries, staffId, specialisation]);

  const byDate = useMemo(() => {
    const m = {};
    filteredEntries.forEach((e) => { (m[e.date] = m[e.date] || []).push(e); });
    return m;
  }, [filteredEntries]);

  const stats = useMemo(() => {
    const published = filteredEntries.filter((e) => e.published).length;
    return {
      shifts: filteredEntries.length,
      published,
      draft: filteredEntries.length - published,
      staffOn: new Set(filteredEntries.map((e) => e.staff_id)).size,
    };
  }, [filteredEntries]);

  const shiftPeriod = (dir) => {
    if (period === 'month') setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + dir, 1));
    else setAnchor((a) => addDays(startOfWeek(a), dir * 7));
  };

  const setPeriodMode = (mode) => {
    setPeriod(mode);
    setAnchor(mode === 'month' ? startOfMonth(new Date()) : startOfWeek(new Date()));
  };

  return {
    period, setPeriodMode, anchor, days, fromIso, toIso, rangeLabel,
    publishFromIso, publishToIso,
    staffId, setStaffId, specialisation, setSpecialisation,
    staffList, specialisations, byDate, stats, loading, reload, shiftPeriod,
    // aliases for gradual rename
    doctorId: staffId, setDoctorId: setStaffId, doctorList: staffList,
  };
}
